import { cva, type VariantProps } from "class-variance-authority";
import { clsx } from "clsx";
import {
  type ForwardedRef,
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";

import { cn, getWhitelabelCustomization } from "common/helpers/utils";

const inputVariants = cva(
  [
    "relative inline-flex items-center justify-center border border-black-100 bg-black-100 text-black-950 outline-2 transition-[background,border] [&_svg]:text-black-950 ",
    "[&_input]:placeholder:text-black-500",
    "hover:border-black-200 hover:bg-black-200",
    "has-[:disabled]:cursor-not-allowed has-[:disabled]:text-black-400",
    "has-[:focus-visible]:!border-purple-400 has-[:focus-visible]:bg-white has-[:focus-visible]:outline-none has-[:focus-visible]:outline-offset-0 has-[:focus-visible]:hover:bg-white",
    "group-data-[error=true]:!border-red-400 group-data-[error=true]:has-[:focus-visible]:outline-red-300",
    "data-[error=true]:!border-red-400 data-[error=true]:has-[:focus-visible]:outline-red-300",
  ],
  {
    variants: {
      variant: {
        sm: "h-8 gap-1 rounded-lg p-2 text-xs-medium [&_svg]:size-4",
        md: "h-10 gap-1.5 rounded-2xl p-2.5 text-sm-medium [&_svg]:size-5",
        lg: "h-11 gap-2 rounded-3xl p-3 text-md-medium [&_svg]:size-5",
      },
    },
    defaultVariants: {
      variant: "md",
    },
  },
);

interface InputProps
  extends InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputVariants> {
  leftComponent?: ReactNode;
  rightComponent?: ReactNode;
  hasError?: boolean;
}

function Input(
  {
    className,
    type,
    variant,
    hasError = false,
    leftComponent = null,
    rightComponent = null,
    ...props
  }: InputProps,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const { whitelabelCustomization } = getWhitelabelCustomization();

  let whitelabelClass: string;
  if (whitelabelCustomization) {
    whitelabelClass = "has-[:focus-visible]:!border-[--whitelabel-color]";
  }

  return (
    <div
      className={cn(
        inputVariants({ variant, className: clsx(className, whitelabelClass) }),
      )}
      data-error={hasError}
    >
      {leftComponent}
      <input
        type={type}
        className="w-full bg-transparent focus-visible:outline-none disabled:cursor-not-allowed"
        ref={ref}
        {...props}
      />
      {rightComponent}
    </div>
  );
}

Input.displayName = "Input";

const InputWithRef = forwardRef(Input);

export { InputWithRef as Input, type InputProps };
